import { forwardRef, useState, useImperativeHandle, type ReactNode } from 'react'
import {
  Tbody,
  Tr,
  Td,
  Checkbox,
  Badge,
  HStack,
  Image,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Divider,
  Portal,
  useBreakpointValue,
} from '@chakra-ui/react'
import { MoreVertical, Edit, Trash2, RotateCcw, Download, ArrowRight, Mail } from 'lucide-react'
import { Link as RouterLink } from 'react-router-dom'
import type { DataTableOptions, Permission } from './types'
import { useThemeColors } from '@/hooks/useThemeColors'

interface TableBodyProps {
  options: DataTableOptions
  rows: any[]
  permissions?: Permission
  onAction?: (id: any, action: string) => void
  onCheckedAll?: (checked: boolean) => void
  customRenderers?: Record<string, (value: any, row?: any) => ReactNode>
  showCheckboxes?: boolean
  showActionsColumn?: boolean
}

export interface TableBodyRef {
  checkedAll: (isCheckedAll: boolean) => void
  getSelectedData: () => number[]
  getSelectedRowsData: () => any[]
}

export const TableBody = forwardRef<TableBodyRef, TableBodyProps>(
  ({ options, rows, permissions, onAction, onCheckedAll, customRenderers, showCheckboxes = true, showActionsColumn = true }, ref) => {
    const isMobile = useBreakpointValue({ base: true, md: false }) ?? false
    const {
      dataFields,
      primaryKey = 'id',
      dataSource,
      softDelete,
      displayInModal,
      otherActions,
      editPath,
    } = options
    const hasPermission = permissions

    const [selectedData, setSelectedData] = useState<number[]>([])
    const [isCheckedAll, setIsCheckedAll] = useState(false)
    const [openActionsForRowId, setOpenActionsForRowId] = useState<any | null>(null)

    const handleCheckboxChange = (rowId: number, checked: boolean) => {
      const updatedSelectedData = checked ? [...selectedData, rowId] : selectedData.filter((id) => id !== rowId)
      setSelectedData(updatedSelectedData)

      if (updatedSelectedData.length === rows.length && rows.length > 0) {
        setIsCheckedAll(true)
        onCheckedAll?.(true)
      } else {
        setIsCheckedAll(false)
        onCheckedAll?.(false)
      }
    }

    const handleCheckAll = (checked: boolean) => {
      const allSelected = checked ? rows.map((row) => row[primaryKey]) : []
      setSelectedData(allSelected)
      setIsCheckedAll(checked)
    }

  useImperativeHandle(ref, () => ({
      checkedAll: handleCheckAll,
      getSelectedData: () => selectedData,
      getSelectedRowsData: () => rows.filter((r) => selectedData.includes(r[primaryKey])),
    }))

    const renderActions = (id: any, row: any, delDate: any, attachment?: string) => {
      const menuItems: JSX.Element[] = []
      const closeMenu = () => setOpenActionsForRowId(null)

      const getOtherActionIcon = (actionName: string) => {
        const name = actionName.toLowerCase()
        if (name.startsWith('edit ')) return <Edit size={16} />
        if (name.startsWith('download ')) return <Download size={16} />
        if (name.startsWith('send ')) return <Mail size={16} />
        return <ArrowRight size={16} />
      }

      if (!delDate && hasPermission?.can_edit) {
        if (displayInModal) {
          menuItems.push(
            <MenuItem
              key="edit"
              icon={<Edit size={16} />}
              onClick={(e) => {
                e.stopPropagation()
                onAction?.(row, 'Edit')
                closeMenu()
              }}
            >
              Edit
            </MenuItem>
          )
        } else {
          const editLink =
            typeof editPath === 'function'
              ? editPath(row)
              : editPath
              ? `${editPath}/${id}`
              : `${dataSource}/${id}`

          menuItems.push(
            <MenuItem
              key="edit"
              as={RouterLink}
              to={editLink}
              icon={<Edit size={16} />}
              onClick={(e) => {
                e.stopPropagation()
                closeMenu()
              }}
            >
              Edit
            </MenuItem>
          )
        }
      }

      if (attachment && !delDate) {
        menuItems.push(
          <MenuItem
            key="download"
            icon={<Download size={16} />}
            as="a"
            href={attachment}
            target="_blank"
            download
            onClick={(e) => {
              e.stopPropagation()
              closeMenu()
            }}
          >
            Download
          </MenuItem>
        )
      }

      if (Array.isArray(otherActions) && !delDate) {
        otherActions
          .filter((action) => !action.show || action.show(row))
          .forEach((action, index) => {
            if (action.onClick) {
              menuItems.push(
                <MenuItem
                  key={`other-${index}`}
                  icon={getOtherActionIcon(action.name)}
                  onClick={(e) => {
                    e.stopPropagation()
                    action.onClick?.(row)
                    closeMenu()
                  }}
                >
                  {action.name}
                </MenuItem>
              )
            } else if (action.link) {
              menuItems.push(
                <MenuItem
                  key={`other-${index}`}
                  as={RouterLink}
                  to={`${action.link}/${id}`}
                  icon={getOtherActionIcon(action.name)}
                  onClick={(e) => {
                    e.stopPropagation()
                    closeMenu()
                  }}
                >
                  {action.name}
                </MenuItem>
              )
            }
          })
      }

      if (menuItems.length > 0 && (hasPermission?.can_delete || delDate)) {
        menuItems.push(<Divider key="divider-1" />)
      }

      if (delDate && hasPermission?.can_delete) {
        menuItems.push(
          <MenuItem
            key="restore"
            icon={<RotateCcw size={16} />}
            color="green.600"
            onClick={(e) => {
              e.stopPropagation()
              onAction?.(row, 'Restore')
              closeMenu()
            }}
          >
            Restore
          </MenuItem>
        )
      }

      if (softDelete && !delDate && hasPermission?.can_delete) {
        menuItems.push(
          <MenuItem
            key="trash"
            icon={<Trash2 size={16} />}
            color="orange.600"
            onClick={(e) => {
              e.stopPropagation()
              if (options.isDeleteAllowedForRow && !options.isDeleteAllowedForRow(row)) {
                options.onDeleteNotAllowed?.(row)
                closeMenu()
                return
              }
              onAction?.(row, 'Trash')
              closeMenu()
            }}
          >
            Trash
          </MenuItem>
        )
      }

      if (softDelete && delDate && hasPermission?.can_delete) {
        menuItems.push(
          <MenuItem
            key="delete"
            icon={<Trash2 size={16} />}
            color="red.600"
            onClick={(e) => {
              e.stopPropagation()
              if (options.isDeleteAllowedForRow && !options.isDeleteAllowedForRow(row)) {
                options.onDeleteNotAllowed?.(row)
                closeMenu()
                return
              }
              onAction?.(row, 'Delete')
              closeMenu()
            }}
          >
            Delete Permanently
          </MenuItem>
        )
      }

      if (menuItems.length === 0) return <Box width="100%" />

      return (
        <Menu
          isLazy
          closeOnBlur={false}
          placement="bottom-end"
          strategy="absolute"
          isOpen={openActionsForRowId === id}
          onClose={() => setOpenActionsForRowId(null)}
        >
          <MenuButton
            as={IconButton}
            aria-label="Actions"
            icon={<MoreVertical size={18} />}
            variant="ghost"
            size="sm"
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              setOpenActionsForRowId((prev: any | null) => (prev === id ? null : id))
            }}
          />
          <Portal>
            <MenuList
              minW="180px"
              maxH="min(70vh, 400px)"
              overflowY="auto"
              zIndex={2000}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              py={1}
              sx={{
                '& [role="menuitem"]': {
                  py: 1,
                  minH: '28px',
                  fontSize: 'sm',
                },
              }}
            >
              {menuItems}
            </MenuList>
          </Portal>
        </Menu>
      )
    }

    const colors = useThemeColors()
    const rowHoverBg = colors.sectionBg
    const rowStripeBg = 'rgba(0, 0, 0, 0.05)'
    const emptyTextColor = colors.textSecondary

    if (rows.length === 0) {
      return (
        <Tbody>
          <Tr>
            <Td colSpan={Object.keys(dataFields).length + (showCheckboxes ? 1 : 0) + (showActionsColumn ? 1 : 0)} textAlign="center" py={8}>
              <Box color={emptyTextColor}>{options.noRecordText || 'No Record Found'}</Box>
            </Td>
          </Tr>
        </Tbody>
      )
    }

    return (
      <Tbody>
        {rows.map((row, rowIndex) => {
          const rowId = row[primaryKey]
          const delDate = row.deleted_at

          return (
            <Tr key={rowIndex} bg={rowIndex % 2 === 1 ? rowStripeBg : 'transparent'} _hover={{ bg: rowHoverBg }}>
              {showCheckboxes && (
                <Td py={2} px={4} fontSize={isMobile ? 'xs' : undefined}>
                  <Checkbox
                    isChecked={selectedData.includes(rowId)}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleCheckboxChange(rowId, e.target.checked)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    colorScheme="blue"
                  />
                </Td>
              )}
              {Object.keys(dataFields).map((field, index) => {
                const columnMaxWidth = dataFields[field].maxWidth || '350px'
                const isFirstColumn = index === 0
                const isDateField =
                  /(_at|date|time)$/i.test(field) || /\b(date|time)\b/i.test(dataFields[field].name || '')

                return (
                  <Td
                    key={field}
                    maxW={columnMaxWidth}
                    className={dataFields[field].className}
                    // verticalAlign="top"
                    whiteSpace={dataFields[field].wrap ? 'normal' : 'nowrap'}
                    wordBreak={dataFields[field].wrap ? 'break-word' : 'normal'}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    fontSize={isMobile ? 'xs' : undefined}
                    py={2}
                    px={4}
                  >
                    {dataFields[field].attachment &&
                      (() => {
                        const attachmentField =
                          typeof dataFields[field].attachment === 'string' ? dataFields[field].attachment : field
                        const imageUrl = row[attachmentField]
                        return imageUrl ? (
                          <Image
                            src={imageUrl}
                            alt={imageUrl || 'Image'}
                            width={{ base: '44px', md: '60px' }}
                            height={{ base: '44px', md: '60px' }}
                            objectFit="cover"
                            borderRadius="md"
                            fallbackSrc="/assets/img/no-image.svg"
                            mr={2}
                            display="inline-block"
                            verticalAlign="middle"
                          />
                        ) : null
                      })()}
                    {customRenderers && customRenderers[field] ? (
                      customRenderers[field](row[field], row)
                    ) : dataFields[field].badge ? (
                      <Badge colorScheme={dataFields[field].badge[row[field]] || 'gray'} variant="solid">
                        {dataFields[field].badgeLabels
                          ? dataFields[field].badgeLabels[row[field]] || row[field]
                          : row[field]}
                      </Badge>
                    ) : (
                      <span>
                        {field === 'file_size' && row[field]
                          ? (() => {
                              const bytes = typeof row[field] === 'number' ? row[field] : parseInt(row[field])
                              if (isNaN(bytes)) return row[field]
                              const units = ['B', 'KB', 'MB', 'GB', 'TB']
                              let size = bytes
                              let unit = 0
                              while (size >= 1024 && unit < units.length - 1) {
                                size /= 1024
                                unit++
                              }
                              return `${size.toFixed(2)} ${units[unit]}`
                            })()
                          : row[field]}
                      </span>
                    )}
                  </Td>
                )
              })}
              {showActionsColumn && (
                <Td
                  width="80px"
                  textAlign="right"
                  fontSize={isMobile ? 'xs' : undefined}
                  py={2}
                  px={4}
                >
                  {renderActions(rowId, row, delDate)}
                </Td>
              )}
            </Tr>
          )
        })}
      </Tbody>
    )
  }
)

TableBody.displayName = 'TableBody'


